import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const joinCode = params.get("join");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: joinCode ? "learner" : "instructor", org_name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, org_name: form.role === "org_admin" ? form.org_name : undefined });
      toast.success("Welcome to Forge");
      if (joinCode) {
        sessionStorage.setItem("forge_auto_redeem", joinCode);
        nav(`/join/${joinCode}`);
      } else {
        nav("/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1724906019868-93ad2c79414f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A0A0A]/40" />
        <Link to="/" className="absolute top-8 left-8 text-white">
          <span className="font-serif text-3xl">Forge<span className="text-[#E63946]">.</span></span>
        </Link>
        <div className="absolute bottom-12 left-8 right-8 text-white">
          <p className="font-serif text-4xl leading-tight">Sell your training. House their training. Build for both — without leaving Forge.</p>
        </div>
      </div>
      <div className="flex items-center justify-center px-8 py-12 bg-[#F9F9F8]">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-8">
            <Link to="/" className="font-serif text-3xl">Forge<span className="text-[#E63946]">.</span></Link>
          </div>
          <div className="overline mb-4">Get started</div>
          <h1 className="editorial-h1 text-5xl mb-10">Create your account.</h1>

          <button onClick={googleLogin} data-testid="google-signup-button" className="w-full btn-secondary mb-6 justify-center">
            Continue with Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[#E4E4E7]" />
            <span className="overline">or with email</span>
            <div className="flex-1 h-px bg-[#E4E4E7]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="overline block mb-2">Full name</label>
              <input data-testid="signup-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Email</label>
              <input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            <div>
              <label className="overline block mb-2">I am a</label>
              <select data-testid="signup-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none">
                <option value="instructor">Trainer / Instructor (build & sell courses)</option>
                <option value="org_admin">Company admin (roll out training to my team)</option>
                <option value="learner">Learner (I'm here to take courses)</option>
              </select>
            </div>
            {form.role === "org_admin" && (
              <div>
                <label className="overline block mb-2">Company name</label>
                <input data-testid="signup-org" required value={form.org_name} onChange={(e) => setForm({ ...form, org_name: e.target.value })} className="w-full px-4 py-3 border border-[#E4E4E7] bg-white focus:border-[#0A0A0A] focus:outline-none" />
              </div>
            )}
            <button type="submit" data-testid="signup-submit" disabled={loading} className="btn-primary w-full justify-center mt-4">
              {loading ? "Creating…" : <>Create account <ArrowUpRight size={16} strokeWidth={1.5} /></>}
            </button>
          </form>
          <div className="mt-8 text-sm text-[#52525B]">
            Already have an account? <Link to="/login" data-testid="link-login" className="text-[#0A0A0A] underline underline-offset-4 hover:text-[#E63946]">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
